import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, useMediaQuery, Container, Stack, Button, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link } from 'react-router-dom';

const words = ['Python', 'PHP', 'JavaScript', 'React', 'Vue.js', 'PostgreSQL', 'Oracle', 'MySQL'];
const techStack = ['React', 'MUI', 'FastAPI', 'PostgreSQL', 'Docker', 'AWS'];

const Home = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isTallDesktop = useMediaQuery('(min-height:1200px)'); 

  const bottomPos = isTallDesktop ? '13%' : '16.5%'; 

  const [displayedText, setDisplayedText] = useState('');
  const [wordIndex, setWordIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!isDesktop) return;

    const typingDelay = deleting ? 100 : 220;
    const pauseTime = 3000; 

    const timeout = setTimeout(() => {
      const currentWord = words[wordIndex];

      if (!deleting && charIndex < currentWord.length) {
        setDisplayedText(currentWord.substring(0, charIndex + 1));
        setCharIndex(prev => prev + 1);
      } else if (deleting && charIndex > 0) {
        setDisplayedText(currentWord.substring(0, charIndex - 1));
        setCharIndex(prev => prev - 1);
      } else if (!deleting && charIndex === currentWord.length) {
        setTimeout(() => setDeleting(true), pauseTime);
      } else if (deleting && charIndex === 0) {
        setDeleting(false);
        setWordIndex(prev => (prev + 1) % words.length);
      }
    }, typingDelay);

    return () => clearTimeout(timeout);
  }, [charIndex, deleting, isDesktop, wordIndex]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundImage: 'url("/assets/background4.webp")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        position: 'relative',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* Gradient overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0, left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(120deg, rgba(2,6,23,0.85) 10%, rgba(2,6,23,0.65) 70%)',
          zIndex: 1,
        }}
      />

      <Container
        sx={{
          position: 'relative',
          zIndex: 2,
          py: { xs: 12, md: 16 },
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Stack spacing={{ xs: 2.5, md: 3 }} sx={{ width: '100%', textAlign: { xs: 'center', md: 'center' }, alignItems: 'center' }}>
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.4rem', md: '4rem' },
              fontFamily: 'Sora, sans-serif',
              fontWeight: 800,
              letterSpacing: -0.5,
              textShadow: '0 6px 24px rgba(0,0,0,0.35)',
            }}
          >
            {t('home.title')}
          </Typography>

        <Typography variant="h5" sx={{
              maxWidth: 760,
              fontSize: { xs: '1.05rem', md: '1.5rem' },
              fontFamily: 'Source Sans 3, sans-serif',
              fontWeight: 600,
              color: 'rgba(255,255,255,0.92)',
            }}
          >
            {t('home.description')}
          </Typography>

          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 2, py: 0.75, borderRadius: 999, border: '1px solid rgba(6,182,212,0.35)', background: 'rgba(6,182,212,0.12)' }}>
            <Typography sx={{ fontFamily: 'Source Sans 3, sans-serif', fontWeight: 600, fontSize: '0.9rem', color: 'rgba(255,255,255,0.85)' }}>
              {t('home.stackLabel')}
            </Typography>
            {isDesktop ? (
              <Typography
                sx={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace',
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  color: '#E6EEF6',
                  minWidth: 120,
                }}
              >
                {displayedText}
              </Typography>
            ) : (
              <Typography sx={{ fontFamily: 'Source Sans 3, sans-serif', fontWeight: 600, fontSize: '0.95rem', color: '#E6EEF6' }}>
                {words.join(' • ')}
              </Typography>
            )}
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button
              component={Link}
              to="/portfolio"
              variant="contained"
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontFamily: 'Sora, sans-serif',
                px: 3,
                py: 1.2,
                background: '#06B6D4',
                color: '#0B1220',
                '&:hover': { background: '#0891B2' },
              }}
            >
              {t('home.ctaPrimary')}
            </Button>
            <Button
              component={Link}
              to="/contact"
              variant="outlined"
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontFamily: 'Sora, sans-serif',
                px: 3,
                py: 1.2,
                color: '#E6EEF6',
                borderColor: 'rgba(255,255,255,0.35)',
                '&:hover': { borderColor: '#06B6D4', color: '#06B6D4' },
              }}
            >
              {t('home.ctaSecondary')}
            </Button>
          </Stack>

          <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', justifyContent: 'center', maxWidth: 720, mt: 1 }}>
            {techStack.map((tech) => (
              <Chip
                key={tech}
                label={tech}
                sx={{
                  fontFamily: 'Source Sans 3, sans-serif',
                  fontWeight: 600,
                  background: 'rgba(255,255,255,0.08)',
                  color: '#E6EEF6',
                  border: '1px solid rgba(255,255,255,0.12)',
                }}
              />
            ))}
          </Stack>
        </Stack>
      </Container>

      {/* subtle floor reflection for depth */}
      {isDesktop && (
        <Box
          sx={{
            position: 'absolute',
            bottom: bottomPos, 
            left: '50%',
            transform: 'translateX(-50%)',
            width: 320,
            height: 40,
            background: 'radial-gradient(closest-side, rgba(6,182,212,0.25), rgba(6,182,212,0))',
            filter: 'blur(10px)',
            zIndex: 1,
          }}
        />
      )}
    </Box>
  );
};

export default Home;
